import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useRole } from "@/contexts/RoleContext";
import { useGrade } from "@/contexts/GradeContext";
import { listSubjects, getStudentTests } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calculator, Leaf, BookOpen, Languages, Sparkles, ArrowRight, FlaskConical, Globe, Layers, ClipboardList, GraduationCap, Library } from "lucide-react";
import TM from "@/components/TM";
import YourSky from "@/components/YourSky";

const SUBJECT_META = {
  "Mathematics":    { icon: Calculator,   color: "from-sky-50 to-sky-100",        tone: "text-sky-700",     bg: "bg-sky-100" },
  "EVS":            { icon: Leaf,         color: "from-emerald-50 to-emerald-100", tone: "text-emerald-700", bg: "bg-emerald-100" },
  "English":        { icon: BookOpen,     color: "from-amber-50 to-amber-100",    tone: "text-amber-700",   bg: "bg-amber-100" },
  "Hindi":          { icon: Languages,    color: "from-rose-50 to-rose-100",      tone: "text-rose-700",    bg: "bg-rose-100" },
  "Science":        { icon: FlaskConical, color: "from-violet-50 to-violet-100",  tone: "text-violet-700",  bg: "bg-violet-100" },
  "Social Science": { icon: Globe,        color: "from-teal-50 to-teal-100",      tone: "text-teal-700",    bg: "bg-teal-100" },
  "Sanskrit":       { icon: Languages,    color: "from-orange-50 to-orange-100",  tone: "text-orange-700",  bg: "bg-orange-100" },
};

function formatLiveDateTime(date) {
  const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "long" });
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true });
  return `${weekday}, ${day} ${month} · ${time}`;
}

export default function WelcomeHome() {
  const { learner } = useRole();
  const { selectedGrade } = useGrade();
  const [subjects, setSubjects] = useState([]);
  const [pendingTestCount, setPendingTestCount] = useState(0);
  const [liveTime, setLiveTime] = useState(() => formatLiveDateTime(new Date()));
  const clockRef = useRef(null);

  useEffect(() => {
    clockRef.current = setInterval(() => setLiveTime(formatLiveDateTime(new Date())), 1000);
    return () => clearInterval(clockRef.current);
  }, []);

  const rawTeacherName = sessionStorage.getItem("teacher_name") || "";
  const teacherFirstName = rawTeacherName ? rawTeacherName.trim().split(/\s+/)[0] : "";

  // School module visibility — null means no school restriction (show all)
  const schoolWhitelist = (() => {
    try {
      const raw = sessionStorage.getItem("school_subject_whitelist");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  // Per-grade subject config (Layer 2)
  const gradeGridActive = sessionStorage.getItem("grade_subject_grid_active") === "true";
  const gradeSubjectGrid = (() => {
    try {
      const raw = sessionStorage.getItem("grade_subject_grid");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  // Effective whitelist: per-grade config takes precedence when active
  const gradeKey = `G${selectedGrade}`;
  const effectiveWhitelist = (() => {
    if (gradeGridActive && gradeSubjectGrid && gradeSubjectGrid[gradeKey]) {
      return gradeSubjectGrid[gradeKey];
    }
    return schoolWhitelist; // null = no restriction
  })();

  const showFMModule = !effectiveWhitelist || effectiveWhitelist.includes("FM Module");
  const showFLN      = !effectiveWhitelist || effectiveWhitelist.includes("FLN");

  useEffect(() => {
    if (!learner?.learner_id) return;
    listSubjects(selectedGrade, learner.learner_id).then(setSubjects).catch(() => setSubjects([]));
  }, [selectedGrade, learner?.learner_id]);

  useEffect(() => {
    if (!learner?.learner_id) return;
    getStudentTests(learner.learner_id)
      .then((data) => {
        const pending = (data.tests || []).filter((t) => t.status === "assigned").length;
        setPendingTestCount(pending);
      })
      .catch(() => {});
  }, [learner?.learner_id]);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="welcome-home">
      <div className="mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-100 text-sky-700 text-xs font-bold uppercase tracking-wider mb-3">
          <Sparkles className="w-3.5 h-3.5" /> Grade {selectedGrade}
        </div>
        <h1 className="font-display text-4xl md:text-6xl font-bold text-slate-800 leading-tight" data-testid="welcome-headline">
          {greeting}, <span className="text-sky-500">{learner?.name || "friend"}</span>!
        </h1>
        <p className="text-slate-500 text-sm mt-2 tabular-nums" data-testid="live-clock-student">
          {liveTime}
        </p>
        <p className="text-slate-600 text-lg md:text-xl mt-2 max-w-2xl">
          Pick a subject to start. We'll go at your pace — short recaps, fun practice, and a friendly hand whenever you need.
        </p>
      </div>

      {/* Your Sky — earned stars from Mitraa moments */}
      <YourSky className="mb-10" />

      {/* My Tests section — shown when tests are assigned */}
      {pendingTestCount > 0 && (
        <Link to="/tests" data-testid="my-tests-banner">
          <Card className="rounded-2xl p-5 mb-8 border-0 bg-gradient-to-r from-violet-50 to-violet-100 hover:-translate-y-0.5 transition-transform cursor-pointer">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center">
                  <ClipboardList className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold text-violet-900">My Tests</p>
                  <p className="text-sm text-violet-600">
                    {pendingTestCount} test{pendingTestCount !== 1 ? "s" : ""} waiting for you
                  </p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-violet-600" />
            </div>
          </Card>
        </Link>
      )}

      {/* My Tests link even when none pending */}
      {pendingTestCount === 0 && (
        <div className="mb-8">
          <Link to="/tests" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-700 transition" data-testid="my-tests-link">
            <ClipboardList className="w-4 h-4" /> My Tests
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {subjects.filter(s => !effectiveWhitelist || effectiveWhitelist.includes(s.subject)).map(s => {
          const meta = SUBJECT_META[s.subject] || { icon: BookOpen, color: "from-slate-50 to-slate-100", tone: "text-slate-700", bg: "bg-slate-100" };
          const Icon = meta.icon;
          return (
            <Link key={s.subject} to={`/subject/${encodeURIComponent(s.subject)}`} data-testid={`subject-tile-${s.subject}`}>
              <Card className={`rounded-3xl p-7 border-0 mit-shadow bg-gradient-to-br ${meta.color} hover:-translate-y-1 transition-transform cursor-pointer`}>
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-2xl ${meta.bg} ${meta.tone} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-display text-2xl font-bold ${meta.tone}`}>{s.subject}</h3>
                    <p className="text-slate-600 text-sm mt-1 flex items-center gap-1.5" data-testid={`subject-counts-${s.subject}`}>
                      {(s.chapter_count ?? 0) !== s.sub_topic_count && (
                        <>
                          <span data-testid={`subject-chapters-${s.subject}`}>
                            {s.chapter_count ?? 0} chapter{(s.chapter_count ?? 0) === 1 ? "" : "s"}
                          </span>
                          <span className="text-slate-300" aria-hidden="true">•</span>
                        </>
                      )}
                      <span data-testid={`subject-subtopics-${s.subject}`}>
                        {s.sub_topic_count} sub-topic{s.sub_topic_count === 1 ? "" : "s"}
                      </span>
                    </p>
                    {teacherFirstName && (
                      <div className="flex items-center gap-1 mt-2" data-testid={`subject-teacher-${s.subject}`}>
                        <GraduationCap className="w-3 h-3 text-slate-400" />
                        <span className="text-xs text-slate-400">{teacherFirstName}</span>
                      </div>
                    )}
                  </div>
                  <ArrowRight className={`w-5 h-5 ${meta.tone} mt-2`} />
                </div>
              </Card>
            </Link>
          );
        })}

        {/* Math Foundations tile — controlled by school whitelist */}
        {showFMModule && (
        <Link to="/math-foundations" data-testid="subject-tile-Math Foundations">
          <Card className="rounded-3xl p-7 border-0 mit-shadow bg-gradient-to-br from-fuchsia-50 to-fuchsia-100 hover:-translate-y-1 transition-transform cursor-pointer">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-fuchsia-100 text-fuchsia-700 flex items-center justify-center flex-shrink-0">
                <Layers className="w-7 h-7" />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-2xl font-bold text-fuchsia-700">Math Foundations</h3>
                <p className="text-slate-600 text-sm mt-1 flex items-center gap-1.5">
                  <span>Number Skills</span>
                  <span className="text-slate-300" aria-hidden="true">•</span>
                  <span className="text-xs font-bold bg-fuchsia-200 text-fuchsia-700 px-2 py-0.5 rounded-full">All Grades</span>
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-fuchsia-700 mt-2" />
            </div>
          </Card>
        </Link>
        )}

        {/* FLN tile — controlled by school whitelist */}
        {showFLN && (
        <Link to="/fln" data-testid="subject-tile-FLN">
          <Card className="rounded-3xl p-7 border-0 mit-shadow hover:-translate-y-1 transition-transform cursor-pointer" style={{ background: "linear-gradient(135deg, #FFF3E0, #FFE0B2)" }}>
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: "#FFE0B2", color: "#E65100" }}>
                <Library className="w-7 h-7" />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-2xl font-bold" style={{ color: "#E65100" }}>Foundational Literacy & Numeracy</h3>
                <p className="text-slate-600 text-sm mt-1 flex items-center gap-1.5">
                  <span>Reading · Writing · Counting</span>
                  <span className="text-slate-300" aria-hidden="true">•</span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "#FFE0B2", color: "#E65100" }}>All Grades</span>
                </p>
              </div>
              <ArrowRight className="w-5 h-5 mt-2" style={{ color: "#E65100" }} />
            </div>
          </Card>
        </Link>
        )}

        {subjects.length === 0 && (
          <div className="col-span-full text-center text-slate-400 py-10" data-testid="no-subjects">Loading subjects…</div>
        )}
      </div>

      <div className="text-center mt-16">
        <Link to="/journey">
          <Button variant="outline" className="rounded-full border-2 border-sky-200 text-sky-700 hover:bg-sky-50 tap-44 px-6" data-testid="see-journey-cta">
            See your journey map
          </Button>
        </Link>
      </div>

      <div className="mt-20 text-center text-xs text-slate-400" data-testid="adiora-footer">
        Powered by Adiora<TM /> Engine
      </div>
    </div>
  );
}
